import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { analytics } from '@modules/analytics';
import type { CardPresetFieldAction } from '@reducers/card-preset-reducer';
import type { CardFieldAction } from '@reducers/card-reducers';
import { useCallback, useState } from 'react';
import type { CardImage } from 'types';

import { EditPicturePopover } from '../EditPicturePopover';
import { type ImageType, typeToFieldId } from '../utilities';
import styles from './CardHeaderPictures.module.scss';
import PictureLockIcon from './PictureLockButton';

/** Returns a user friendly name for the image type */
function toFriendlyName(imgType: ImageType): string {
  switch (imgType) {
    case 'cover':
      return 'Cover Photo';
    case 'logo':
      return 'Company Logo';
    case 'profile':
      return 'Profile Picture';
  }
}

/** Placeholder component when there is no picture uploaded */
const EmptyPicture: React.FC<
  React.PropsWithChildren<{ children?: never; type: ImageType; required: boolean }>
> = ({ type, required }) => {
  return (
    <div className={styles['empty-picture']}>
      <div className={styles['empty-picture-icon-container']}>
        <FontAwesomeIcon icon={faPlus} size="sm" />
      </div>
      <div>{toFriendlyName(type)}</div>
      <div className={styles['required-label']}>{required ? 'Required' : null}</div>
    </div>
  );
};
interface PicturePreviewProps {
  canLock: boolean | undefined;
  cardId: string | undefined;
  cardFieldsDispatch:
    | ((action: CardFieldAction) => void)
    | ((action: CardPresetFieldAction) => void);
  image: CardImage | undefined;
  isLocked: boolean;
  isPreset: boolean;
  linkedAttributes?: string[] | undefined;
  toggleFieldLock: (id: string, toggleState?: boolean) => void;
  onPopoverClose?: () => void;
  onRemove: () => void;
  onSave: (image: CardImage) => void;
  onSelect: (image: CardImage) => void;
  onSelectOpen?: (type: ImageType) => void;
  onUpdate: (image: CardImage, quality: 'high' | 'low' | undefined) => void;
  presetId: string | undefined;
  required: boolean;
  targetNode: HTMLElement | undefined;
  type: ImageType;
  userId: string;
  orgId: string;
}

interface PicturePreviewButtonProps {
  src?: string;
  onClick: () => void;
  isLocked?: boolean;
  canLock?: boolean;
  isPreset?: boolean;
  required: boolean;
  type: ImageType;
  toggleLocked?: () => void;
}

export const PicturePreviewButton = ({
  ref,
  canLock,
  isLocked,
  onClick,
  required,
  src,
  type,
  toggleLocked,
}: PicturePreviewButtonProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const handleLockClicked = (e: React.MouseEvent) => {
    toggleLocked?.();
    e.preventDefault();
  };

  const handleClick = useCallback(() => {
    analytics.track({
      name: src ? 'editpicture_clicked' : 'addpicture_clicked',
      properties: { card_picture_type: type },
    });
    onClick();
  }, [onClick, type, src]);
  return (
    <>
      <div className={styles['picture-preview-button-container']}>
        <PictureLockIcon
          locked={isLocked}
          canLock={canLock}
          position="top-right"
          background={true}
          onClick={handleLockClicked}
        />
        <button
          ref={ref}
          className={styles['picture-preview']}
          onClick={handleClick}
          data-locked={isLocked}
          data-can-lock={canLock}
          data-empty={!src}
        >
          {src ? (
            // relative position styling for placing the lock icon
            <div className={styles['image-container']}>
              <img src={src} alt={`Preview of ${type}`} />
              <div className={styles['picture-label']}>
                <span className={styles['picture-label-text']}>{toFriendlyName(type)}</span>
                {(!isLocked || canLock) && (
                  <span className={styles['picture-label-icon']}>
                    <FontAwesomeIcon icon={faPencil} />
                  </span>
                )}
              </div>
            </div>
          ) : (
            <EmptyPicture type={type} required={required} />
          )}
        </button>
      </div>
    </>
  );
};

PicturePreviewButton.displayName = 'hello';

export const PicturePreview: React.FC<React.PropsWithChildren<PicturePreviewProps>> = ({
  canLock,
  cardId,
  cardFieldsDispatch,
  image,
  isLocked,
  toggleFieldLock,
  isPreset,
  linkedAttributes,
  onRemove,
  onSave,
  onSelect,
  onSelectOpen,
  onPopoverClose,
  onUpdate,
  presetId,
  required,
  targetNode,
  type,
  userId,
  orgId,
}) => {
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const src = image?.editedImageUrl || image?.originalImageUrl;
  let linked = false;

  if (type === 'profile') {
    linked = linkedAttributes?.includes('profile_picture_img') || false;
  }
  if (type === 'logo') {
    linked = linkedAttributes?.includes('company_logo_img') || false;
  }
  if (type === 'cover') {
    linked = linkedAttributes?.includes('cover_photo_img') || false;
  }

  const handleUnlink = () => {
    switch (type) {
      case 'profile':
        cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTE', id: 'profile_picture_img' });
        break;
      case 'logo':
        cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTE', id: 'company_logo_img' });
        break;
      case 'cover':
        cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTE', id: 'cover_photo_img' });
        break;
    }
  };
  const handleToggleLocked = () => {
    toggleFieldLock(typeToFieldId(type), !isLocked);
  };

  return (
    <>
      <PicturePreviewButton
        canLock={canLock}
        isLocked={isLocked}
        isPreset={isPreset}
        onClick={() => setPopoverOpen(canLock || !isLocked)}
        required={required}
        src={src}
        toggleLocked={handleToggleLocked}
        type={type}
      />

      <EditPicturePopover
        cardId={cardId}
        isPreset={isPreset}
        targetNode={targetNode}
        show={isPopoverOpen}
        onClose={() => {
          setPopoverOpen(false);
          onPopoverClose?.();
        }}
        onUpdate={onUpdate}
        imageReference={image}
        onRemove={onRemove}
        onSave={onSave}
        onSelect={onSelect}
        onSelectOpen={onSelectOpen}
        type={type}
        canLock={canLock || false}
        linked={linked}
        onUnlink={handleUnlink}
        presetId={presetId}
        userId={userId}
        orgId={orgId}
      />
    </>
  );
};

export const PicturePreviewContainer: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  return <div className={styles['picture-preview-container']}>{children}</div>;
};
